import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../core/providers/AuthProvider';
import { useSettingsValidation } from '../../hooks/useSettingsValidation';
import { useUpdateProfile } from '../../hooks/useUpdateProfile';
import { Button } from '../../../../core/design-system/components/Button';
import { Input } from '../../../../core/design-system/components/Input';
import { Toast } from '../../../../core/design-system/components/Toast';
import CityInputs from '../../../../shared/components/CityInputs/CityInputs';
import PhoneField from '../../components/PhoneField';
import './styles.css';

const AccountSettingsPage = () => {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const { updateProfile } = useUpdateProfile();
  const [isEditing, setIsEditing] = useState(false);
  const { errors, validateForm, clearErrors } = useSettingsValidation();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  
  const [formData, setFormData] = useState({
    displayName: user?.displayName || '',
    email: user?.email || '',
    phone: {
      phone: user?.phone || '',
      phoneVerified: user?.phoneVerified || false
    },
    bio: user?.bio || '',
    location: user?.location || null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name !== 'phone' && errors[name]) {
      clearErrors(name);
    }
  };

  const handlePhoneChange = (phoneData) => {
    setFormData(prev => ({
      ...prev,
      phone: phoneData
    }));
    clearErrors('phone');
  };

  const handleLocationChange = (location) => {
    console.log('DEBUG - Location sélectionnée:', location);
    
    // Vérifier si les coordonnées sont manquantes et si placeId est défini
    if (!location?.location?.lat && !location?.location?.lng && location?.placeId) {
      console.log('DEBUG - Coordonnées manquantes, tentative de récupération via API...');
      // Vous pouvez ajouter ici une logique pour récupérer les coordonnées via l'API Google Maps si nécessaire
    }
    
    setFormData(prev => ({
      ...prev,
      location
    }));
    if (errors.location) {
      clearErrors('location');
    }
  };

  const handleCancel = () => {
    setFormData({
      displayName: user?.displayName || '',
      email: user?.email || '',
      phone: {
        phone: user?.phone || '',
        phoneVerified: user?.phoneVerified || false
      },
      bio: user?.bio || '',
      location: user?.location || null
    });
    clearErrors();
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm(formData)) {
      setShowToast(true);
      setToastMessage(t('pages.account.validationError'));
      setToastType('error');
      return;
    }

    try {
      await updateProfile({
        displayName: formData.displayName,
        email: formData.email,
        phone: formData.phone.phone,
        phoneVerified: formData.phone.phoneVerified,
        bio: formData.bio,
        location: formData.location
      });
      
      setIsEditing(false);
      clearErrors();
      setShowToast(true);
      setToastMessage(t('pages.account.saveSuccess'));
      setToastType('success');
    } catch (error) {
      setShowToast(true);
      setToastMessage(error.message || t('pages.account.saveError'));
      setToastType('error');
    }
  };

  return (
    <div className="account-settings">
      <form onSubmit={handleSubmit} className="account-form">
        <div className="form-group">
          <label>{t('pages.account.fields.name')}</label>
          <Input
            type="text"
            name="displayName"
            value={formData.displayName}
            onChange={handleChange}
            disabled={!isEditing}
            error={errors.displayName}
          />
        </div>

        <div className="form-group">
          <label>{t('pages.account.fields.email')}</label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            disabled={!isEditing}
            error={errors.email}
          />
        </div>

        <div className="form-group">
          <label>{t('pages.account.fields.phone')}</label>
          <PhoneField
            value={{
              phone: formData.phone.phone,
              phoneVerified: formData.phone.phoneVerified
            }}
            onChange={handlePhoneChange}
            error={errors.phone}
            disabled={!isEditing}
          />
        </div>

        <div className="form-group">
          <label>{t('pages.account.fields.bio')}</label>
          <textarea
            name="bio"
            value={formData.bio}
            onChange={handleChange}
            disabled={!isEditing}
            className="form-textarea"
            rows={4}
          />
        </div>

        <div className="form-group">
          <label>{t('pages.account.fields.location')}</label>
          {isEditing ? (
            <CityInputs
              value={formData.location}
              onChange={handleLocationChange}
              placeholder={t('pages.account.fields.location_placeholder')}
              error={errors.location}
            />
          ) : (
            <Input
              type="text"
              value={formData.location?.address || t('common.notSet')}
              disabled
            />
          )}
        </div>

        <div className="form-actions">
          {isEditing ? (
            <>
              <Button 
                type="button" 
                variant="secondary"
                size="sm"
                onClick={handleCancel}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                size="sm"
              >
                {t('common.save')}
              </Button>
            </>
          ) : (
            <Button 
              type="button" 
              variant="primary"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              {t('pages.account.edit')}
            </Button>
          )}
        </div>
      </form>
      {showToast && (
        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setShowToast(false)}
        />
      )}
    </div>
  );
};

export default AccountSettingsPage; 